// src/modules/tenant/AssetsGallery.tsx
import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { API_ROOT_URL } from "../../config/api";
import ShareAssetsModal from "../tenant/ShareAssetsModal";
import AssignEventModal from "./AssignEventModal";
import { ToastContainer } from "react-toastify";
import {
  File,
  FileArchive,
  FileImage,
  FileSpreadsheet,
  FileText,
  FileVideo,
  Search,
  Zap,
  FolderKanban,
  Calendar,
  CalendarPlus,
  Package,
  Tag,
  LayoutGrid,
  List as ListIcon,
  X,
} from "lucide-react";
import "react-toastify/dist/ReactToastify.css";

interface GalleryAsset {
  id: string;
  original_filename: string;
  proxy_key?: string | null;
  master_key?: string | null;
  preview_url?: string | null;
  is_archived?: boolean;
  status: string;
}

interface FilterOption {
  id: string;
  name: string;
}

type AssetKind =
  | "image"
  | "video"
  | "pdf"
  | "spreadsheet"
  | "archive"
  | "document";

const IMAGE_EXTENSIONS = new Set([
  "jpg",
  "jpeg",
  "png",
  "gif",
  "webp",
  "bmp",
  "svg",
  "tif",
  "tiff",
]);
const VIDEO_EXTENSIONS = new Set([
  "mp4",
  "mpeg",
  "mpg",
  "mov",
  "avi",
  "mkv",
  "webm",
  "m4v",
]);
const SPREADSHEET_EXTENSIONS = new Set(["xls", "xlsx", "csv", "ods"]);
const ARCHIVE_EXTENSIONS = new Set(["zip", "rar", "7z", "tar", "gz"]);

const getExtension = (filename: string) =>
  filename.split(".").pop()?.toLowerCase() || "file";

const getAssetKind = (filename: string): AssetKind => {
  const extension = getExtension(filename);
  if (IMAGE_EXTENSIONS.has(extension)) return "image";
  if (VIDEO_EXTENSIONS.has(extension)) return "video";
  if (extension === "pdf") return "pdf";
  if (SPREADSHEET_EXTENSIONS.has(extension)) return "spreadsheet";
  if (ARCHIVE_EXTENSIONS.has(extension)) return "archive";
  return "document";
};

const FILE_STYLE: Record<
  AssetKind,
  { label: string; color: string; background: string }
> = {
  image: {
    label: "IMAGE",
    color: "text-violet-600",
    background: "bg-violet-50",
  },
  video: { label: "VIDEO", color: "text-blue-600", background: "bg-blue-50" },
  pdf: { label: "PDF", color: "text-red-600", background: "bg-red-50" },
  spreadsheet: {
    label: "SHEET",
    color: "text-emerald-600",
    background: "bg-emerald-50",
  },
  archive: {
    label: "ARCHIVE",
    color: "text-amber-600",
    background: "bg-amber-50",
  },
  document: {
    label: "DOCUMENT",
    color: "text-slate-600",
    background: "bg-slate-50",
  },
};

const AssetPreview: React.FC<{ asset: GalleryAsset; compact?: boolean }> = ({
  asset,
  compact = false,
}) => {
  const [previewFailed, setPreviewFailed] = useState(false);
  const kind = getAssetKind(asset.original_filename);
  const style = FILE_STYLE[kind];
  const iconSize = compact ? 21 : 30;
  const iconProps = { size: iconSize, strokeWidth: 1.8 };

  if (asset.proxy_key && !previewFailed && kind === "image") {
    return (
      <img
        src={asset.proxy_key}
        alt={asset.original_filename}
        className="w-full h-full object-cover"
        onError={() => setPreviewFailed(true)}
      />
    );
  }

  if (asset.proxy_key && !previewFailed && kind === "video") {
    return (
      <video
        src={asset.proxy_key}
        className="w-full h-full object-cover bg-slate-900"
        muted
        controls={!compact}
        preload="metadata"
        onError={() => setPreviewFailed(true)}
      />
    );
  }

  const Icon =
    kind === "image"
      ? FileImage
      : kind === "video"
        ? FileVideo
        : kind === "pdf"
          ? FileText
          : kind === "spreadsheet"
            ? FileSpreadsheet
            : kind === "archive"
              ? FileArchive
              : File;

  return (
    <div
      className={`w-full h-full flex ${compact ? "items-center justify-center" : "flex-col items-center justify-center gap-1"} ${style.background} ${style.color}`}
    >
      <Icon {...iconProps} />
      {!compact && (
        <span className="text-[9px] font-bold tracking-wider">
          {style.label}
        </span>
      )}
    </div>
  );
};

function useDebouncedValue<T>(value: T, delay = 400): T {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const timer = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(timer);
  }, [value, delay]);
  return debounced;
}

const ASSET_STATUSES = [
  "received",
  "validating",
  "rejected",
  "queued",
  "transcoding",
  "completed",
  "failed",
];

const STATUS_LABELS: Record<string, string> = {
  all: "All Status",
  received: "Received",
  validating: "Validating",
  rejected: "Rejected",
  queued: "Queued",
  transcoding: "Transcoding",
  completed: "Completed",
  failed: "Failed",
};

const AssetsGallery: React.FC = () => {
  const navigate = useNavigate();
  const [assets, setAssets] = useState<GalleryAsset[]>([]);
  const [loading, setLoading] = useState(false);

  const [searchInput, setSearchInput] = useState("");
  const debouncedSearch = useDebouncedValue(searchInput, 400);
  const [statusFilter, setStatusFilter] = useState("all");
  const [campaignFilter, setCampaignFilter] = useState("all");
  const [eventFilter, setEventFilter] = useState("all");
  const [productFilter, setProductFilter] = useState("all");
  const [tagFilter, setTagFilter] = useState("all");
  const [tagOptions, setTagOptions] = useState<FilterOption[]>([]);
  const [campaignOptions, setCampaignOptions] = useState<FilterOption[]>([]);
  const [eventOptions, setEventOptions] = useState<FilterOption[]>([]);
  const [productOptions, setProductOptions] = useState<FilterOption[]>([]);
  const [viewMode, setViewMode] = useState<"grid" | "list">("grid");

  // Dropdown open state + refs for outside-click close
  const [statusDropdownOpen, setStatusDropdownOpen] = useState(false);
  const [campaignDropdownOpen, setCampaignDropdownOpen] = useState(false);
  const [eventDropdownOpen, setEventDropdownOpen] = useState(false);
  const [productDropdownOpen, setProductDropdownOpen] = useState(false);
  const [tagDropdownOpen, setTagDropdownOpen] = useState(false);

  const statusDropdownRef = React.useRef<HTMLDivElement | null>(null);
  const campaignDropdownRef = React.useRef<HTMLDivElement | null>(null);
  const eventDropdownRef = React.useRef<HTMLDivElement | null>(null);
  const productDropdownRef = React.useRef<HTMLDivElement | null>(null);
  const tagDropdownRef = React.useRef<HTMLDivElement | null>(null);

  React.useEffect(() => {
    const handler = (e: MouseEvent) => {
      const t = e.target as Node;
      if (statusDropdownRef.current && !statusDropdownRef.current.contains(t))
        setStatusDropdownOpen(false);
      if (
        campaignDropdownRef.current &&
        !campaignDropdownRef.current.contains(t)
      )
        setCampaignDropdownOpen(false);
      if (eventDropdownRef.current && !eventDropdownRef.current.contains(t))
        setEventDropdownOpen(false);
      if (productDropdownRef.current && !productDropdownRef.current.contains(t))
        setProductDropdownOpen(false);
      if (tagDropdownRef.current && !tagDropdownRef.current.contains(t))
        setTagDropdownOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const selectedCampaignName =
    campaignFilter === "all"
      ? "All Campaigns"
      : campaignOptions.find((o) => o.id === campaignFilter)?.name ||
        "All Campaigns";
  const selectedEventName =
    eventFilter === "all"
      ? "All Events"
      : eventOptions.find((o) => o.id === eventFilter)?.name || "All Events";
  const selectedProductName =
    productFilter === "all"
      ? "All Products"
      : productOptions.find((o) => o.id === productFilter)?.name ||
        "All Products";
  const selectedTagName =
    tagFilter === "all"
      ? "All Tags"
      : tagOptions.find((o) => o.id === tagFilter)?.name || "All Tags";

  const hasActiveAssetFilters =
    statusFilter !== "all" ||
    campaignFilter !== "all" ||
    eventFilter !== "all" ||
    productFilter !== "all" ||
    tagFilter !== "all" ||
    searchInput.trim() !== "";

  const clearAllAssetFilters = () => {
    setStatusFilter("all");
    setCampaignFilter("all");
    setEventFilter("all");
    setProductFilter("all");
    setTagFilter("all");
    setSearchInput("");
  };

  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(24); // default for grid
  const [limitTouched, setLimitTouched] = useState(false);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(1);

  // --- Asset sharing (additive, does not touch existing gallery logic) ---
  const [selectedAssetIds, setSelectedAssetIds] = useState<Set<string>>(
    new Set(),
  );
  const [isShareModalOpen, setIsShareModalOpen] = useState(false);
  const [isAssignModalOpen, setIsAssignModalOpen] = useState(false);
  // Bumped after an assignment so filtered views reload
  const [reloadKey, setReloadKey] = useState(0);

  const toggleAssetSelection = (id: string) => {
    setSelectedAssetIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  const clearSelection = () => setSelectedAssetIds(new Set());

  const allOnPageSelected =
    assets.length > 0 && assets.every((a) => selectedAssetIds.has(a.id));
  const toggleSelectAllOnPage = () =>
    setSelectedAssetIds(
      allOnPageSelected ? new Set() : new Set(assets.map((a) => a.id)),
    );

  // Switch default page size when the view toggles, unless user picked one manually
  useEffect(() => {
    if (!limitTouched) {
      setLimit(viewMode === "grid" ? 24 : 10);
    }
    setPage(1);
  }, [viewMode]);

  // Reset to page 1 whenever search / filter / limit changes
  useEffect(() => {
    setPage(1);
  }, [
    debouncedSearch,
    statusFilter,
    campaignFilter,
    eventFilter,
    productFilter,
    tagFilter,
    limit,
  ]);

  // Clear selection when filters/page change so stale/off-screen ids aren't shared by mistake
  useEffect(() => {
    clearSelection();
  }, [
    debouncedSearch,
    statusFilter,
    campaignFilter,
    eventFilter,
    productFilter,
    tagFilter,
    page,
    limit,
    viewMode,
  ]);

  useEffect(() => {
    const loadFilterOptions = async () => {
      try {
        const accessToken = localStorage.getItem("accessToken");
        const res = await fetch(`${API_ROOT_URL}/assets/filter-options`, {
          headers: {
            ...(accessToken ? { Authorization: `Bearer ${accessToken}` } : {}),
          },
        });
        if (!res.ok) throw new Error(`Request failed with ${res.status}`);

        const result = await res.json();
        setCampaignOptions(result.data?.campaigns || []);
        setEventOptions(result.data?.events || []);
        setProductOptions(result.data?.products || []);
        setTagOptions(result.data?.tags || []);
      } catch (err) {
        console.error("Failed to load asset filter options", err);
      }
    };

    loadFilterOptions();
  }, []);

  useEffect(() => {
    const loadAssets = async () => {
      setLoading(true);
      try {
        const params = new URLSearchParams({
          page: String(page),
          limit: String(limit),
        });
        if (debouncedSearch) params.set("search", debouncedSearch);
        if (statusFilter !== "all") params.set("status", statusFilter);
        if (campaignFilter !== "all") params.set("campaign_id", campaignFilter);
        if (eventFilter !== "all") params.set("event_id", eventFilter);
        if (productFilter !== "all") params.set("product_id", productFilter);
        if (tagFilter !== "all") params.set("tag_id", tagFilter);

        const accessToken = localStorage.getItem("accessToken"); // same key used in ShareAssetsModal
        const res = await fetch(`${API_ROOT_URL}/assets?${params.toString()}`, {
          headers: {
            ...(accessToken ? { Authorization: `Bearer ${accessToken}` } : {}),
          },
        });
        if (!res.ok) throw new Error(`Request failed with ${res.status}`);
        const result = await res.json();
        const assetList = result.data || result;
        setAssets(
          assetList.map((a: any) => ({
            id: String(a.id),
            original_filename: a.original_filename,
            proxy_key: a.preview_url || null,
            master_key: a.master_key,
            status: a.status,
            preview_url: a.preview_url,
            is_archived: a.is_archived === true,
          })),
        );
        if (result.pagination) {
          setTotal(result.pagination.total);
          setTotalPages(result.pagination.totalPages);
        }
      } catch (err) {
        console.error("Failed to load assets", err);
      } finally {
        setLoading(false);
      }
    };
    loadAssets();
  }, [
    page,
    limit,
    debouncedSearch,
    statusFilter,
    campaignFilter,
    eventFilter,
    productFilter,
    tagFilter,
    reloadKey,
  ]);

  const from = total === 0 ? 0 : (page - 1) * limit + 1;
  const to = Math.min(page * limit, total);
  const limitOptions = viewMode === "grid" ? [24, 48, 96] : [10, 25, 50];

  return (
    <div className="space-y-6">
      <ToastContainer position="top-right" autoClose={3000} />
      {/* Top bar with Asset Ingestion button */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-800">Assets</h2>
          <p className="text-sm text-gray-500 mt-1">
            Browse all uploaded assets
          </p>
        </div>
        <div className="flex items-center gap-3">
          {selectedAssetIds.size > 0 && (
            <button
              onClick={() => setIsShareModalOpen(true)}
              className="px-5 py-2.5 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 transition flex items-center gap-2 shadow-sm"
            >
              🔗 Share ({selectedAssetIds.size})
            </button>
          )}
          <button
            onClick={() => setIsAssignModalOpen(true)}
            disabled={selectedAssetIds.size === 0}
            title={
              selectedAssetIds.size === 0
                ? "Tick one or more assets first"
                : "Assign the selected assets to a campaign / event"
            }
            className="px-5 py-2.5 rounded-lg transition flex items-center gap-2 font-medium bg-orange-500 text-white shadow-sm hover:bg-orange-600 focus:outline-none focus-visible:ring-2 focus-visible:ring-orange-300 disabled:bg-gray-200 disabled:text-gray-500 disabled:shadow-none disabled:cursor-not-allowed"
          >
            <CalendarPlus size={16} />
            Assign Event
            {selectedAssetIds.size > 0 ? ` (${selectedAssetIds.size})` : ""}
          </button>
          <button
            onClick={() => navigate("/tenant/dashboard?tab=assets")}
            className="px-5 py-2.5 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition flex items-center gap-2 shadow-sm"
          >
            📤 Asset Ingestion
          </button>
        </div>
      </div>

      {/* Search + Filters + View toggle bar */}
      <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-3 flex flex-wrap items-center justify-between gap-3">
        <div className="flex flex-wrap gap-3 items-center">
          {/* Search */}
          <div className="relative">
            <Search
              size={16}
              className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none"
            />
            <input
              type="text"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              placeholder="Search assets..."
              className="pl-9 pr-4 py-2.5 border border-gray-300 rounded-xl text-sm focus:ring-2 focus:ring-purple-500 focus:border-purple-500 w-64 transition"
            />
          </div>

          {/* Status dropdown */}
          <div ref={statusDropdownRef} className="relative inline-block">
            <button
              type="button"
              onClick={() => setStatusDropdownOpen((p) => !p)}
              className={`w-44 flex items-center justify-between gap-3 px-4 py-2.5 bg-white border rounded-xl text-sm font-medium transition-all duration-200 ${
                statusDropdownOpen
                  ? "border-purple-500 ring-2 ring-purple-100 shadow-sm"
                  : "border-gray-200 hover:border-gray-300"
              }`}
            >
              <div className="flex items-center gap-2 min-w-0">
                <Zap size={16} className="text-gray-400 shrink-0" />
                <span className="truncate text-gray-700">
                  {STATUS_LABELS[statusFilter] || "All Status"}
                </span>
              </div>
              <span
                className={`text-gray-400 transition-transform duration-200 ${
                  statusDropdownOpen ? "rotate-180" : ""
                }`}
              >
                ▾
              </span>
            </button>

            {statusDropdownOpen && (
              <div className="absolute z-50 mt-2 left-0 w-44 bg-white border border-gray-200 rounded-xl shadow-xl overflow-hidden">
                <div className="max-h-72 overflow-y-auto p-1.5 [scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden">
                  {["all", ...ASSET_STATUSES].map((s) => {
                    const isSelected = statusFilter === s;
                    return (
                      <button
                        key={s}
                        type="button"
                        onClick={() => {
                          setStatusFilter(s);
                          setStatusDropdownOpen(false);
                        }}
                        className={`w-full flex items-center justify-between gap-2 px-3 py-2.5 rounded-lg text-sm text-left transition-colors ${
                          isSelected
                            ? "bg-purple-50 text-purple-700 font-semibold"
                            : "text-gray-700 hover:bg-gray-50"
                        }`}
                      >
                        <span className="truncate">{STATUS_LABELS[s]}</span>
                        {isSelected && (
                          <span className="text-purple-600 shrink-0">✓</span>
                        )}
                      </button>
                    );
                  })}
                </div>
              </div>
            )}
          </div>

          {/* Campaign dropdown */}
          <div ref={campaignDropdownRef} className="relative inline-block">
            <button
              type="button"
              onClick={() => setCampaignDropdownOpen((p) => !p)}
              className={`w-48 flex items-center justify-between gap-3 px-4 py-2.5 bg-white border rounded-xl text-sm font-medium transition-all duration-200 ${
                campaignDropdownOpen
                  ? "border-purple-500 ring-2 ring-purple-100 shadow-sm"
                  : "border-gray-200 hover:border-gray-300"
              }`}
            >
              <div className="flex items-center gap-2 min-w-0">
                <FolderKanban size={16} className="text-gray-400 shrink-0" />
                <span className="truncate text-gray-700">
                  {selectedCampaignName}
                </span>
              </div>
              <span
                className={`text-gray-400 transition-transform duration-200 ${
                  campaignDropdownOpen ? "rotate-180" : ""
                }`}
              >
                ▾
              </span>
            </button>

            {campaignDropdownOpen && (
              <div className="absolute z-50 mt-2 left-0 w-48 bg-white border border-gray-200 rounded-xl shadow-xl overflow-hidden">
                <div className="max-h-72 overflow-y-auto p-1.5 [scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden">
                  <button
                    type="button"
                    onClick={() => {
                      setCampaignFilter("all");
                      setCampaignDropdownOpen(false);
                    }}
                    className={`w-full flex items-center justify-between gap-2 px-3 py-2.5 rounded-lg text-sm text-left transition-colors ${
                      campaignFilter === "all"
                        ? "bg-purple-50 text-purple-700 font-semibold"
                        : "text-gray-700 hover:bg-gray-50"
                    }`}
                  >
                    <span>All Campaigns</span>
                    {campaignFilter === "all" && (
                      <span className="text-purple-600 shrink-0">✓</span>
                    )}
                  </button>

                  {campaignOptions.map((option) => {
                    const isSelected = campaignFilter === option.id;
                    return (
                      <button
                        key={option.id}
                        type="button"
                        onClick={() => {
                          setCampaignFilter(option.id);
                          setCampaignDropdownOpen(false);
                        }}
                        className={`w-full flex items-center justify-between gap-2 px-3 py-2.5 rounded-lg text-sm text-left transition-colors ${
                          isSelected
                            ? "bg-purple-50 text-purple-700 font-semibold"
                            : "text-gray-700 hover:bg-gray-50"
                        }`}
                      >
                        <span className="truncate">{option.name}</span>
                        {isSelected && (
                          <span className="text-purple-600 shrink-0">✓</span>
                        )}
                      </button>
                    );
                  })}

                  {campaignOptions.length === 0 && (
                    <div className="px-3 py-3 text-sm text-gray-500 text-center">
                      No campaigns available
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>

          {/* Tag dropdown */}
          <div ref={tagDropdownRef} className="relative inline-block">
            <button
              type="button"
              onClick={() => setTagDropdownOpen((p) => !p)}
              className={`w-44 flex items-center justify-between gap-3 px-4 py-2.5 bg-white border rounded-xl text-sm font-medium transition-all duration-200 ${
                tagDropdownOpen
                  ? "border-purple-500 ring-2 ring-purple-100 shadow-sm"
                  : "border-gray-200 hover:border-gray-300"
              }`}
            >
              <div className="flex items-center gap-2 min-w-0">
                <Tag size={16} className="text-gray-400 shrink-0" />
                <span className="truncate text-gray-700">
                  {selectedTagName}
                </span>
              </div>
              <span
                className={`text-gray-400 transition-transform duration-200 ${
                  tagDropdownOpen ? "rotate-180" : ""
                }`}
              >
                ▾
              </span>
            </button>

            {tagDropdownOpen && (
              <div className="absolute z-50 mt-2 left-0 w-44 bg-white border border-gray-200 rounded-xl shadow-xl overflow-hidden">
                <div className="max-h-72 overflow-y-auto p-1.5 [scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden">
                  {[{ id: "all", name: "All Tags" }, ...tagOptions].map(
                    (option) => {
                      const isSelected = tagFilter === option.id;
                      return (
                        <button
                          key={option.id}
                          type="button"
                          onClick={() => {
                            setTagFilter(option.id);
                            setTagDropdownOpen(false);
                          }}
                          className={`w-full flex items-center justify-between gap-2 px-3 py-2.5 rounded-lg text-sm text-left transition-colors ${
                            isSelected
                              ? "bg-purple-50 text-purple-700 font-semibold"
                              : "text-gray-700 hover:bg-gray-50"
                          }`}
                        >
                          <span className="truncate">{option.name}</span>
                          {isSelected && (
                            <span className="text-purple-600 shrink-0">✓</span>
                          )}
                        </button>
                      );
                    },
                  )}

                  {tagOptions.length === 0 && (
                    <div className="px-3 py-3 text-sm text-gray-500 text-center">
                      No tags available
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>

          {/* Event dropdown */}
          <div ref={eventDropdownRef} className="relative inline-block">
            <button
              type="button"
              onClick={() => setEventDropdownOpen((p) => !p)}
              className={`w-44 flex items-center justify-between gap-3 px-4 py-2.5 bg-white border rounded-xl text-sm font-medium transition-all duration-200 ${
                eventDropdownOpen
                  ? "border-purple-500 ring-2 ring-purple-100 shadow-sm"
                  : "border-gray-200 hover:border-gray-300"
              }`}
            >
              <div className="flex items-center gap-2 min-w-0">
                <Calendar size={16} className="text-gray-400 shrink-0" />
                <span className="truncate text-gray-700">
                  {selectedEventName}
                </span>
              </div>
              <span
                className={`text-gray-400 transition-transform duration-200 ${
                  eventDropdownOpen ? "rotate-180" : ""
                }`}
              >
                ▾
              </span>
            </button>

            {eventDropdownOpen && (
              <div className="absolute z-50 mt-2 left-0 w-44 bg-white border border-gray-200 rounded-xl shadow-xl overflow-hidden">
                <div className="max-h-72 overflow-y-auto p-1.5 [scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden">
                  <button
                    type="button"
                    onClick={() => {
                      setEventFilter("all");
                      setEventDropdownOpen(false);
                    }}
                    className={`w-full flex items-center justify-between gap-2 px-3 py-2.5 rounded-lg text-sm text-left transition-colors ${
                      eventFilter === "all"
                        ? "bg-purple-50 text-purple-700 font-semibold"
                        : "text-gray-700 hover:bg-gray-50"
                    }`}
                  >
                    <span>All Events</span>
                    {eventFilter === "all" && (
                      <span className="text-purple-600 shrink-0">✓</span>
                    )}
                  </button>

                  {eventOptions.map((option) => {
                    const isSelected = eventFilter === option.id;
                    return (
                      <button
                        key={option.id}
                        type="button"
                        onClick={() => {
                          setEventFilter(option.id);
                          setEventDropdownOpen(false);
                        }}
                        className={`w-full flex items-center justify-between gap-2 px-3 py-2.5 rounded-lg text-sm text-left transition-colors ${
                          isSelected
                            ? "bg-purple-50 text-purple-700 font-semibold"
                            : "text-gray-700 hover:bg-gray-50"
                        }`}
                      >
                        <span className="truncate">{option.name}</span>
                        {isSelected && (
                          <span className="text-purple-600 shrink-0">✓</span>
                        )}
                      </button>
                    );
                  })}

                  {eventOptions.length === 0 && (
                    <div className="px-3 py-3 text-sm text-gray-500 text-center">
                      No events available
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>

          {/* Product dropdown */}
          <div ref={productDropdownRef} className="relative inline-block">
            <button
              type="button"
              onClick={() => setProductDropdownOpen((p) => !p)}
              className={`w-44 flex items-center justify-between gap-3 px-4 py-2.5 bg-white border rounded-xl text-sm font-medium transition-all duration-200 ${
                productDropdownOpen
                  ? "border-purple-500 ring-2 ring-purple-100 shadow-sm"
                  : "border-gray-200 hover:border-gray-300"
              }`}
            >
              <div className="flex items-center gap-2 min-w-0">
                <Package size={16} className="text-gray-400 shrink-0" />
                <span className="truncate text-gray-700">
                  {selectedProductName}
                </span>
              </div>
              <span
                className={`text-gray-400 transition-transform duration-200 ${
                  productDropdownOpen ? "rotate-180" : ""
                }`}
              >
                ▾
              </span>
            </button>

            {productDropdownOpen && (
              <div className="absolute z-50 mt-2 left-0 w-44 bg-white border border-gray-200 rounded-xl shadow-xl overflow-hidden">
                <div className="max-h-72 overflow-y-auto p-1.5 [scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden">
                  <button
                    type="button"
                    onClick={() => {
                      setProductFilter("all");
                      setProductDropdownOpen(false);
                    }}
                    className={`w-full flex items-center justify-between gap-2 px-3 py-2.5 rounded-lg text-sm text-left transition-colors ${
                      productFilter === "all"
                        ? "bg-purple-50 text-purple-700 font-semibold"
                        : "text-gray-700 hover:bg-gray-50"
                    }`}
                  >
                    <span>All Products</span>
                    {productFilter === "all" && (
                      <span className="text-purple-600 shrink-0">✓</span>
                    )}
                  </button>

                  {productOptions.map((option) => {
                    const isSelected = productFilter === option.id;
                    return (
                      <button
                        key={option.id}
                        type="button"
                        onClick={() => {
                          setProductFilter(option.id);
                          setProductDropdownOpen(false);
                        }}
                        className={`w-full flex items-center justify-between gap-2 px-3 py-2.5 rounded-lg text-sm text-left transition-colors ${
                          isSelected
                            ? "bg-purple-50 text-purple-700 font-semibold"
                            : "text-gray-700 hover:bg-gray-50"
                        }`}
                      >
                        <span className="truncate">{option.name}</span>
                        {isSelected && (
                          <span className="text-purple-600 shrink-0">✓</span>
                        )}
                      </button>
                    );
                  })}

                  {productOptions.length === 0 && (
                    <div className="px-3 py-3 text-sm text-gray-500 text-center">
                      No products available
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>

          {/* Clear filters — appears only when a filter is active */}
          {hasActiveAssetFilters && (
            <button
              type="button"
              onClick={clearAllAssetFilters}
              className="inline-flex items-center gap-1.5 px-4 py-2.5 text-sm font-medium text-gray-700 border border-gray-300 rounded-xl hover:bg-gray-100 transition"
            >
              <X size={14} />
              Clear Filters
            </button>
          )}
        </div>

        {/* View toggle */}
        <div className="flex gap-1 bg-gray-100 rounded-xl p-1">
          <button
            onClick={() => setViewMode("grid")}
            className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium transition ${
              viewMode === "grid"
                ? "bg-white text-purple-600 shadow-sm"
                : "text-gray-500 hover:text-gray-700"
            }`}
          >
            <LayoutGrid size={14} />
            Grid
          </button>
          <button
            onClick={() => setViewMode("list")}
            className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium transition ${
              viewMode === "list"
                ? "bg-white text-purple-600 shadow-sm"
                : "text-gray-500 hover:text-gray-700"
            }`}
          >
            <ListIcon size={14} />
            List
          </button>
        </div>
      </div>

      {!loading && assets.length > 0 && (
        <div className="flex items-center gap-3 text-sm text-gray-600 -mb-2">
          <label className="inline-flex items-center gap-2 cursor-pointer select-none">
            <input
              type="checkbox"
              checked={allOnPageSelected}
              onChange={toggleSelectAllOnPage}
              className="h-4 w-4 accent-purple-600 cursor-pointer"
            />
            Select all on this page
          </label>
          <span className="text-gray-300">|</span>
          {selectedAssetIds.size > 0 ? (
            <>
              <span className="font-medium text-gray-800">
                {selectedAssetIds.size} selected
              </span>
              <button
                type="button"
                onClick={clearSelection}
                className="text-purple-600 hover:underline"
              >
                Clear selection
              </button>
            </>
          ) : (
            <span className="text-gray-500">
              Click assets to select them, then use Share or Assign Event
            </span>
          )}
        </div>
      )}

      {/* Asset grid / list */}
      {loading ? (
        <p className="text-sm text-gray-400 text-center py-12">
          Loading assets...
        </p>
      ) : assets.length === 0 ? (
        <div className="text-center py-12 bg-gray-50 rounded-lg">
          <div className="text-6xl mb-3">🗂️</div>
          <p className="text-gray-500">No assets found</p>
        </div>
      ) : viewMode === "grid" ? (
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 xl:grid-cols-8 gap-4">
          {assets.map((asset) => (
            <div
              key={asset.id}
              onClick={(e) => {
                // Let video controls work without toggling selection
                if ((e.target as HTMLElement).closest("video")) return;
                toggleAssetSelection(asset.id);
              }}
              className={`relative bg-white rounded-lg shadow-sm border overflow-hidden hover:shadow-md transition-all duration-300 cursor-pointer ${
                selectedAssetIds.has(asset.id)
                  ? "border-purple-500 ring-2 ring-purple-200"
                  : "border-gray-200 hover:border-gray-300"
              }`}
            >
              <span className="absolute top-1.5 left-1.5 z-10 flex items-center justify-center h-6 w-6 rounded-md bg-white/90 shadow-sm">
                <input
                  type="checkbox"
                  checked={selectedAssetIds.has(asset.id)}
                  onChange={() => toggleAssetSelection(asset.id)}
                  onClick={(e) => e.stopPropagation()}
                  className="h-4 w-4 accent-purple-600 cursor-pointer"
                  aria-label={`Select ${asset.original_filename}`}
                />
              </span>
              <div className="h-24 bg-gray-100 flex items-center justify-center overflow-hidden">
                <AssetPreview asset={asset} />
              </div>
              <div className="p-2">
                <p className="text-xs font-medium text-gray-800 truncate">
                  {asset.original_filename}
                </p>
                {asset.is_archived && (
                  <p className="text-[10px] text-indigo-600 mt-1">
                    ❄️ Archived in Glacier
                  </p>
                )}
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="bg-white rounded-lg border border-gray-200 divide-y divide-gray-100">
          {assets.map((asset) => (
            <div
              key={asset.id}
              onClick={(e) => {
                if ((e.target as HTMLElement).closest("video")) return;
                toggleAssetSelection(asset.id);
              }}
              className={`flex items-center gap-4 px-4 py-3 transition cursor-pointer ${
                selectedAssetIds.has(asset.id) ? "bg-purple-50" : "hover:bg-gray-50"
              }`}
            >
              <input
                type="checkbox"
                checked={selectedAssetIds.has(asset.id)}
                onChange={() => toggleAssetSelection(asset.id)}
                onClick={(e) => e.stopPropagation()}
                className="h-4 w-4 accent-purple-600 cursor-pointer flex-shrink-0"
                aria-label={`Select ${asset.original_filename}`}
              />
              <div className="h-11 w-11 flex-shrink-0 bg-gray-100 rounded-lg overflow-hidden flex items-center justify-center border border-gray-100">
                <AssetPreview asset={asset} compact />
              </div>
              <p className="text-sm font-medium text-gray-800 truncate flex-1">
                {asset.original_filename}
              </p>
              {asset.is_archived && (
                <span className="text-xs text-indigo-600">
                  ❄️ Archived in Glacier
                </span>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Pagination bar */}
      {total > 0 && (
        <div className="flex items-center justify-between flex-wrap gap-3">
          <span className="text-sm text-gray-500">
            Showing {from}-{to} of {total}
          </span>
          <div className="flex items-center gap-2">
            <button
              disabled={page <= 1}
              onClick={() => setPage((p) => p - 1)}
              className="px-3 py-1.5 border rounded-lg disabled:opacity-40"
            >
              Prev
            </button>
            <span className="text-sm text-gray-600">
              Page {page} of {totalPages}
            </span>
            <button
              disabled={page >= totalPages}
              onClick={() => setPage((p) => p + 1)}
              className="px-3 py-1.5 border rounded-lg disabled:opacity-40"
            >
              Next
            </button>
            <select
              value={limit}
              onChange={(e) => {
                setLimitTouched(true);
                setLimit(Number(e.target.value));
              }}
              className="ml-2 px-2 py-1.5 border rounded-lg text-sm"
            >
              {limitOptions.map((o) => (
                <option key={o} value={o}>
                  {o}
                </option>
              ))}
            </select>
          </div>
        </div>
      )}

      {isAssignModalOpen && (
        <AssignEventModal
          selectedAssets={assets.filter((a) => selectedAssetIds.has(a.id))}
          onClose={() => setIsAssignModalOpen(false)}
          onAssigned={() => {
            setIsAssignModalOpen(false);
            clearSelection();
            setReloadKey((k) => k + 1);
          }}
        />
      )}

      {isShareModalOpen && (
        <ShareAssetsModal
          selectedAssets={assets.filter((a) => selectedAssetIds.has(a.id))}
          onClose={() => setIsShareModalOpen(false)}
          onShared={() => {
            setIsShareModalOpen(false);
            clearSelection();
          }}
        />
      )}
    </div>
  );
};

export default AssetsGallery;